<template>
  <div :class="containerClass">
    <page-header :title="title" :container-class="null" :show-back="true" class="mb-32pt" />

    <div class="page-section pt-0">
      <b-card>
        <b-overlay :show="isLoading" rounded="sm" opacity="0.4">
          <template #overlay>
            <div class="text-center">
              <b-spinner class="mb-2" />
              <div>Loading attempts...</div>
            </div>
          </template>

          <div class="quiz-header-section mb-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div class="h5 mb-1 font-weight-bold text-dark">{{ quizTitle || 'Quiz' }}</div>
                <div class="text-muted small">
                  <i class="fas fa-user-graduate mr-1"></i>
                  Student ID: {{ studentId }}
                </div>
              </div>
              <div class="total-attempts-badge" v-if="attempts.length">
                <div class="attempts-number">{{ attempts.length }}</div>
                <div class="attempts-label">Total Attempts</div>
              </div>
            </div>

            <!-- Attempts Summary Stats -->
            <div class="stats-cards" v-if="averageScore !== null || bestPercent !== null || bestScore !== null">
              <div class="stat-card stat-average" v-if="averageScore !== null">
                <div class="stat-icon">
                  <i class="fas fa-chart-line"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-label">Average Marks</div>
                  <div class="stat-value">{{ averageScore }}/{{ totalMarks }}</div>
                </div>
              </div>

              <div class="stat-card stat-best" v-if="bestPercent !== null">
                <div class="stat-icon">
                  <i class="fas fa-trophy"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-label">Best Performance</div>
                  <div class="stat-value">{{ formatScore(bestPercent) }}</div>
                </div>
              </div>

              <div class="stat-card stat-highest" v-if="bestScore !== null">
                <div class="stat-icon">
                  <i class="fas fa-star"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-label">Highest Marks</div>
                  <div class="stat-value">{{ bestScore }}/{{ totalMarks }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!attempts.length" class="empty-state">
            No attempts found for this quiz.
          </div>

          <div v-else>
            <b-card no-body v-for="attempt in attempts" :key="attempt.id" class="mb-3 attempt-card">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  v-b-toggle="`attempt-${attempt.id}`"
                  variant="link"
                  class="text-left text-decoration-none attempt-header"
                >
                  <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex align-items-center">
                      <div class="chevron-icon mr-3">
                        <i class="fas fa-chevron-right"></i>
                      </div>
                      <div class="attempt-title">Attempt #{{ attempt.attempt_number || '-' }}</div>
                    </div>
                    <div class="d-flex align-items-center">
                      <span class="score-badge mr-4" :class="getPerformanceClass(attempt.score)">Score: {{ formatScore(attempt.score) }}</span>
                      <span class="date-text">{{ formatDateTime(attempt.submitted_at) }}</span>
                    </div>
                  </div>
                </b-button>
              </b-card-header>
              <b-collapse :id="`attempt-${attempt.id}`" accordion="attempts-accordion" role="tabpanel">
                <b-card-body class="attempt-content">
                  <!-- Attempt Summary -->
                  <div class="attempt-summary mb-4">
                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                      <div class="d-flex align-items-center mb-2">
                        <div class="performance-circle mr-3" :class="getPerformanceClass(attempt.score)">
                          <div class="percentage">{{ formatScore(attempt.score) }}</div>
                          <div class="label">Score</div>
                        </div>
                        <div>
                          <div class="font-weight-bold text-dark mb-1">Attempt #{{ attempt.attempt_number }}</div>
                          <div class="text-muted small">
                            <i class="far fa-calendar-alt mr-1"></i>
                            {{ formatDateTime(attempt.submitted_at) }}
                          </div>
                        </div>
                      </div>
                      <div class="attempt-meta">
                        <b-badge variant="light" class="px-3 py-2">
                          <i class="far fa-question-circle mr-1"></i>
                          {{ (attempt.questions || []).length }} Questions
                        </b-badge>
                      </div>
                    </div>
                  </div>

                  <!-- Questions -->
                  <div class="questions-container">
                    <div v-for="(question, idx) in attempt.questions || []" :key="question.id || idx" class="question-card mb-3">
                      <div class="question-header">
                        <div class="d-flex align-items-start justify-content-between">
                          <div class="d-flex align-items-start flex-grow-1">
                            <div class="question-number-badge">{{ idx + 1 }}</div>
                            <div class="question-text flex-grow-1" v-safe-html="question.question_text"></div>
                          </div>
                          <div class="question-type-badge">
                            <span class="type-label">
                              {{ question.type === 'text' ? 'TEXT' : question.type === 'true_false' ? 'T/F' : 'MCQ' }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="question-body">
                        <!-- Text answers -->
                        <div v-if="question.type === 'text'" class="text-answer-section">
                          <div class="answer-box">
                            <div class="answer-box-header">
                              <i class="far fa-edit mr-2"></i>Student's Answer
                            </div>
                            <div class="answer-box-content" v-safe-html="question.answer_text || question.student_answer || '-'"></div>
                          </div>
                          <div v-if="Array.isArray(question.options) && question.options.length" class="mt-3">
                            <div class="acceptable-answers-header">
                              <i class="fas fa-check-double mr-2"></i>Acceptable Answers
                            </div>
                            <div class="acceptable-answers-list">
                              <div v-for="(opt, aIdx) in question.options" :key="aIdx" class="acceptable-answer-item">
                                <i class="fas fa-check text-success mr-2"></i>
                                <span v-safe-html="opt.option_text"></span>
                                <small v-if="opt.explanation" class="explanation-inline ml-2" v-safe-html="opt.explanation"></small>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- MCQ / TF options -->
                        <div v-else-if="Array.isArray(question.options) && question.options.length" class="options-section">
                          <!-- No option selected warning -->
                          <div v-if="!hasAnyOptionSelected(question)" class="no-answer-alert mb-3">
                            <div class="alert-icon">
                              <i class="fas fa-exclamation-circle"></i>
                            </div>
                            <div class="alert-content">
                              <div class="alert-title">No Answer Selected</div>
                              <div class="alert-message">You didn't choose any option.</div>
                            </div>
                          </div>

                          <div
                            v-for="(opt, oIdx) in question.options"
                            :key="oIdx"
                            class="option-item"
                            :class="{
                              'option-correct': opt.is_correct,
                              'option-selected': isOptionSelected(question, opt),
                              'option-selected-wrong': isOptionSelected(question, opt) && !opt.is_correct
                            }"
                          >
                            <div class="option-icon">
                              <i
                                :class="[
                                  'fas',
                                  opt.is_correct ? 'fa-check-circle' : (isOptionSelected(question, opt) ? 'fa-times-circle' : 'fa-circle')
                                ]"
                              />
                            </div>
                            <div class="option-content flex-grow-1">
                              <div class="option-text" v-safe-html="opt.option_text"></div>
                            </div>
                            <div class="option-badges">
                              <span v-if="isOptionSelected(question, opt)" class="option-label" :class="opt.is_correct ? 'label-correct' : 'label-wrong'">
                                {{ opt.is_correct ? 'Your Answer' : 'Your Answer' }}
                              </span>
                              <span v-else-if="opt.is_correct" class="option-label label-correct">
                                Correct
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Explanation -->
                        <div v-if="question.explanation" class="explanation-section">
                          <div class="explanation-header">
                            <i class="fas fa-lightbulb mr-2"></i>Explanation
                          </div>
                          <div class="explanation-content" v-safe-html="question.explanation" />
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
        </b-overlay>
      </b-card>
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '@/common/utils';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import axios from 'axios';
import { get } from 'lodash';

export default {
  name: 'QuizAttempts',
  components: { PageHeader },
  extends: Page,
  data() {
    return {
      title: 'Quiz Attempts',
      isLoading: false,
      attempts: [],
      quizTitle: '',
      averageScore: null,
      bestPercent: null,
      bestScore: null,
      totalMarks: null,
    };
  },
  computed: {
    enrollmentId() {
      return this.$route.params.enrollmentId;
    },
    quizId() {
      return this.$route.params.quizId;
    },
    studentId() {
      return this.$route.query.student_id;
    },
  },
  methods: {
    get,
    formatDateTime,
    isOptionSelected(question, option) {
      // Prefer explicit is_selected if present
      if (option && typeof option.is_selected === 'boolean') return option.is_selected;
      // Fallback to selected_option_ids array on question
      const selectedIds = this.get(question, 'selected_option_ids');
      const optionId = option && (option.id || option.option_id);
      if (Array.isArray(selectedIds) && optionId !== undefined && optionId !== null) {
        return selectedIds.includes(optionId);
      }
      return false;
    },
    hasAnyOptionSelected(question) {
      if (!question || !Array.isArray(question.options)) return false;
      return question.options.some(opt => this.isOptionSelected(question, opt));
    },
    formatScore(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return `${num}%`;
    },
    getPerformanceClass(score) {
      const num = Number(score);
      if (!Number.isFinite(num)) return 'performance-neutral';
      if (num >= 90) return 'performance-excellent';
      if (num >= 75) return 'performance-good';
      if (num >= 60) return 'performance-average';
      return 'performance-poor';
    },
    async fetchAttempts() {
      if (!this.quizId || !this.studentId) return;
      this.isLoading = true;
      try {
        // Build query parameters
        const params = {
          student_id: this.studentId,
          quiz_id: this.quizId,
        };

        // Add context ID if present in route query
        const { chapter_id, lesson_id, course_id } = this.$route.query;
        if (chapter_id) {
          params.chapter_id = chapter_id;
        } else if (lesson_id) {
          params.lesson_id = lesson_id;
        } else if (course_id) {
          params.course_id = course_id;
        }

        // Backend endpoint provided by BE
        const res = await axios.get('api/lms/quiz-results/attempts-by-student/', { params });
        const payload = get(res, 'data.data') || get(res, 'data') || {};
        this.attempts = get(payload, 'attempts', []);
        this.quizTitle = get(payload, 'quiz.title') || get(payload, 'quiz_title') || 'Quiz Attempts';
        this.averageScore = get(payload, 'average_score', null);
        this.bestPercent = get(payload, 'best_percent', null);
        this.bestScore = get(payload, 'best_score', null);
        this.totalMarks = get(payload, 'total_marks', null);
        this.title = `${this.quizTitle} • Attempts`;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.get(e, 'response.data.message') || 'Failed to load attempts.' });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchAttempts();
  },
  watch: {
    '$route.fullPath'() {
      this.fetchAttempts();
    },
  },
};
</script>

<style scoped>
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

/* Quiz Header Section */
.quiz-header-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.total-attempts-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  min-width: 100px;
}

.total-attempts-badge .attempts-number {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.total-attempts-badge .attempts-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  font-weight: 600;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 10px;
  padding: 1.25rem;
  border: 2px solid;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-card .stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  font-size: 1.25rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.stat-card .stat-content {
  flex-grow: 1;
}

.stat-card .stat-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 0.25rem;
  opacity: 0.8;
}

.stat-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

/* Average Score Card */
.stat-average {
  border-color: #93c5fd;
  background: linear-gradient(135deg, #dbeafe 0%, #ffffff 100%);
}

.stat-average .stat-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
}

.stat-average .stat-label {
  color: #1e40af;
}

.stat-average .stat-value {
  color: #1d4ed8;
}

/* Best Performance Card */
.stat-best {
  border-color: #fcd34d;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
}

.stat-best .stat-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #ffffff;
}

.stat-best .stat-label {
  color: #92400e;
}

.stat-best .stat-value {
  color: #b45309;
}

/* Highest Score Card */
.stat-highest {
  border-color: #86efac;
  background: linear-gradient(135deg, #d1fae5 0%, #ffffff 100%);
}

.stat-highest .stat-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
}

.stat-highest .stat-label {
  color: #065f46;
}

.stat-highest .stat-value {
  color: #047857;
}

/* Collapsible Card Styling */
.attempt-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.attempt-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.attempt-header {
  padding: 1rem 1.25rem;
  color: #374151;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.attempt-header:hover {
  background-color: #f9fafb;
}

.attempt-header:focus {
  box-shadow: none;
  outline: none;
}

/* Chevron Icon Animation */
.chevron-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
  color: #6b7280;
}

.attempt-header:not(.collapsed) .chevron-icon {
  transform: rotate(90deg);
}

/* Attempt Title and Meta */
.attempt-title {
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
  white-space: nowrap;
}

.score-badge {
  font-weight: 600;
  font-size: 0.8125rem;
  white-space: nowrap;
  color: #374151;
  background: #f3f4f6;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
}

.date-text {
  font-size: 0.875rem;
  color: #9ca3af;
  white-space: nowrap;
}

/* Content Area */
.attempt-content {
  padding: 1.5rem;
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
}

/* Attempt Summary */
.attempt-summary {
  background: #ffffff;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.performance-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid var(--secondary-color, #6b7280);
  background: #ffffff;
  transition: all 0.3s ease;
}

.performance-circle .percentage {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 2px;
  color: var(--secondary-color, #6b7280);
}

.performance-circle .label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  opacity: 0.8;
  color: var(--secondary-color, #6b7280);
}

.attempt-meta .badge {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Questions Container */
.questions-container {
  margin-top: 1rem;
}

.question-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.question-header {
  background: #ffffff;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.question-number-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  background: var(--primary-color, #3b82f6);
  color: #ffffff;
  font-weight: 700;
  border-radius: 6px;
  margin-right: 1rem;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.question-text {
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
  line-height: 1.5;
  padding-top: 4px;
}

.question-type-badge {
  margin-left: 1rem;
  flex-shrink: 0;
}

.type-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
}

.question-body {
  padding: 1.25rem;
}

/* Text Answer Section */
.text-answer-section {
  margin-top: 0.5rem;
}

.answer-box {
  background: #f8fafc;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  overflow: hidden;
}

.answer-box-header {
  background: transparent;
  color: #0369a1;
  padding: 0.75rem 1rem 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.8125rem;
}

.answer-box-content {
  padding: 0 1rem 0.875rem 1rem;
  color: #1e293b;
  min-height: 3rem;
  line-height: 1.6;
}

.acceptable-answers-header {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #059669;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.acceptable-answers-list {
  background: #f0fdf4;
  border-left: 3px solid #10b981;
  border-radius: 4px;
  padding: 0.875rem 1rem;
}

.acceptable-answer-item {
  display: flex;
  align-items: flex-start;
  padding: 0.375rem 0;
  color: #065f46;
  line-height: 1.5;
}

.acceptable-answer-item:not(:last-child) {
  border-bottom: 1px solid #d1fae5;
  margin-bottom: 0.375rem;
  padding-bottom: 0.625rem;
}

.explanation-inline {
  color: #6b7280;
  font-style: italic;
}

/* Options Section */
.options-section {
  margin-top: 0.75rem;
}

.no-answer-alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fbbf24;
}

.no-answer-alert .alert-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.25rem;
  color: #f59e0b;
}

.no-answer-alert .alert-content {
  flex: 1;
}

.no-answer-alert .alert-title {
  font-weight: 700;
  font-size: 0.9375rem;
  color: #92400e;
  margin-bottom: 0.25rem;
}

.no-answer-alert .alert-message {
  font-size: 0.875rem;
  color: #78350f;
  line-height: 1.4;
}


.option-item {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  margin-bottom: 0.625rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  transition: all 0.2s ease;
}

.option-item:hover {
  background: #fafafa;
}

.option-correct {
  background: #ffffff;
  border-color: #10b981;
}

.option-selected-wrong {
  background: #ffffff;
  border-color: #ef4444;
}

.option-selected.option-correct {
  background: #ffffff;
  border-color: #10b981;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 0.875rem;
  font-size: 1rem;
  flex-shrink: 0;
}

.option-icon .fa-check-circle {
  color: #10b981;
}

.option-icon .fa-times-circle {
  color: #ef4444;
}

.option-icon .fa-circle {
  color: #d1d5db;
  font-size: 0.5rem;
}

.option-content {
  color: #111827;
  line-height: 1.5;
}

.option-text {
  font-weight: 500;
}

.option-badges {
  margin-left: auto;
  flex-shrink: 0;
}

.option-label {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  letter-spacing: 0.3px;
}

.label-correct {
  color: #10b981;
  background: #f0fdf4;
}

.label-wrong {
  color: #ef4444;
  background: #fef2f2;
}

/* Explanation Section */
.explanation-section {
  margin-top: 1.25rem;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  overflow: hidden;
}

.explanation-header {
  background: transparent;
  color: #92400e;
  padding: 0.75rem 1rem 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
}

.explanation-header .fa-lightbulb {
  color: #f59e0b;
}

.explanation-content {
  padding: 0 1rem 0.875rem 1rem;
  color: #78350f;
  line-height: 1.6;
  font-size: 0.9375rem;
}

/* Responsive */
@media (max-width: 768px) {
  .quiz-header-section {
    padding: 1rem;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .stat-card .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
    margin-right: 0.875rem;
  }

  .stat-card .stat-value {
    font-size: 1.25rem;
  }

  .total-attempts-badge {
    min-width: 90px;
    padding: 0.625rem 1rem;
  }

  .total-attempts-badge .attempts-number {
    font-size: 1.25rem;
  }

  .performance-circle {
    width: 70px;
    height: 70px;
  }

  .performance-circle .percentage {
    font-size: 1.125rem;
  }

  .question-number-badge {
    min-width: 28px;
    height: 28px;
    font-size: 0.8125rem;
  }

  .option-label {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
}

@media (max-width: 576px) {
  .stats-cards {
    margin-top: 0.75rem;
  }

  .stat-card .stat-label {
    font-size: 0.7rem;
  }

  .stat-card .stat-value {
    font-size: 1.125rem;
  }
}
</style>


